import {
	Action,
	configureStore,
	ThunkAction,
	PreloadedState,
	StateFromReducersMapObject,
} from '@reduxjs/toolkit';
import { FilterSlice } from './reducers/filters';
import { GlobalStateSlice } from './reducers/index';
import { PropertiesSlice } from './reducers/properties';
import { RecommendedPropertiesSlice } from './reducers/recommendedProperties';
import { FilterDataSlice } from './reducers/filtersData';
import { FiltersAvailableSlice } from './reducers/filtersAvailable';
import { LeadSlice } from './reducers/lead';
import { SessionSlice } from './reducers/session';
import { FavoritesSlice } from './reducers/favorites';
import { UserSearchSlice } from './reducers/userSearch';
import { useMemo } from 'react';

const apiEndpoint: string = process.env.NEXT_PUBLIC_HOME_BASE_API ?? ""
export interface IThunkApiExtra {
	apiEndpoint: string,
	services: () => Promise<typeof import("../../services.setup.client")>
}

const reducer = {
	GlobalState: GlobalStateSlice.reducer,
	PropertyState: PropertiesSlice.reducer,
	RecommendedPropertiesState: RecommendedPropertiesSlice.reducer,
	FiltersAvailableState: FiltersAvailableSlice.reducer,
	FilterState: FilterSlice.reducer,
	FilterDataState: FilterDataSlice.reducer,
	LeadState: LeadSlice.reducer,
	SessionState: SessionSlice.reducer,
	FavoritesState: FavoritesSlice.reducer,
	UserSearchState: UserSearchSlice.reducer
}

export type RootState = StateFromReducersMapObject<typeof reducer>

function initStore(preloadedState?: PreloadedState<RootState>) {
	return configureStore({
		reducer,
		preloadedState,
		devTools: process.env.NODE_ENV !== 'production',
		middleware: getDefaultMiddleware => getDefaultMiddleware({
			thunk: {
				extraArgument: {
					apiEndpoint,
					services: async () => (await import('../../services.setup.client'))
				}
			}
		})
	});
}

export type AppStore = ReturnType<typeof initStore>
export type AppDispatch = AppStore["dispatch"];

let store: ReturnType<typeof initStore> | undefined;
// This could be helpful
// https://github.com/vercel/next.js/blob/next-13/examples/with-redux-thunk/store.js
export function setupStore(preloadedState?: PreloadedState<RootState>) {
	let _store = store ?? initStore(preloadedState)

	// After navigating to a page with an initial Redux state, merge that state
	// with the current state in the store, and create a new store
	if (preloadedState && store) {
		_store = initStore({
			...store.getState(),
			...preloadedState,
		})
		// Reset the current store
		store = undefined
	}

	// For SSG and SSR always create a new store
	if (typeof window === 'undefined') return _store
	// Create the store once in the client
	if (!store) store = _store

	return _store

	// return initStore(preloadedState)
}

export function useStore(initialState? : PreloadedState<RootState>) {
	const store = useMemo(() => setupStore(initialState), [initialState])
	return store
}

export const selectGlobal = (state: RootState) => state.GlobalState;

export type AppThunk<ReturnType = void> = ThunkAction<
	ReturnType,
	RootState,
	unknown | { apiEndpoint: string | undefined },
	Action<string>
>;